import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const STRATEGIES = [
  { value: "all", label: "All" },
  { value: "fib-vwap", label: "Fib+VWAP (Nifty)" },
  { value: "fib-vwap-banknifty", label: "Fib+VWAP (Bank Nifty)" },
  { value: "fib-vwap-sensex", label: "Fib+VWAP (Sensex)" },
];

const ANALYSIS_TYPES = [
  { value: "all", label: "All Trades" },
  { value: "wins", label: "Winning Trades" },
  { value: "losses", label: "Losing Trades" },
  { value: "stops", label: "Stop Loss Exits" },
  { value: "day", label: "Specific Day" },
];

const CHAT_STORAGE_KEY = "ai_chat_history";

/**
 * Simple markdown-to-HTML renderer.
 * Handles **bold**, ## headers, - bullets, `code`, and newlines.
 */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${elements.length}`} style={{ margin: "8px 0", paddingLeft: 20 }}>
          {listItems.map((li, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{formatInline(li)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const formatInline = (str) => {
    const parts = [];
    let remaining = str;
    let key = 0;
    const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
    let match;
    let lastIndex = 0;

    while ((match = regex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        parts.push(remaining.slice(lastIndex, match.index));
      }
      if (match[1]) {
        parts.push(<strong key={key++}>{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(<em key={key++}>{match[4]}</em>);
      } else if (match[5]) {
        parts.push(
          <code key={key++} style={{ background: "var(--panel3)", padding: "1px 5px", borderRadius: 3, fontSize: "0.9em" }}>
            {match[6]}
          </code>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
      parts.push(remaining.slice(lastIndex));
    }
    return parts.length > 0 ? parts : str;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      elements.push(<div key={`br-${i}`} style={{ height: 8 }} />);
      continue;
    }

    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(
        <h4 key={`h3-${i}`} style={{ color: "var(--text2)", margin: "16px 0 8px", fontSize: 15 }}>
          {formatInline(trimmed.slice(4))}
        </h4>
      );
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h3 key={`h2-${i}`} style={{ color: "#f1f5f9", margin: "20px 0 10px", fontSize: 17, borderBottom: "1px solid var(--border2)", paddingBottom: 6 }}>
          {formatInline(trimmed.slice(3))}
        </h3>
      );
    } else if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(
        <h2 key={`h1-${i}`} style={{ color: "#f8fafc", margin: "20px 0 10px", fontSize: 20 }}>
          {formatInline(trimmed.slice(2))}
        </h2>
      );
    } else if (/^[-*] /.test(trimmed)) {
      listItems.push(trimmed.slice(2));
    } else if (/^\d+\.\s/.test(trimmed)) {
      flushList();
      elements.push(
        <div key={`ol-${i}`} style={{ margin: "4px 0", paddingLeft: 8 }}>
          {formatInline(trimmed)}
        </div>
      );
    } else {
      flushList();
      elements.push(
        <p key={`p-${i}`} style={{ margin: "6px 0", lineHeight: 1.6 }}>
          {formatInline(trimmed)}
        </p>
      );
    }
  }
  flushList();
  return elements;
}

export default function AiAnalyzer() {
  const [aiStatus, setAiStatus] = useState(null);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("");
  const [source, setSource] = useState("live");
  const [strategy, setStrategy] = useState("fib-vwap");
  const [analysisType, setAnalysisType] = useState("all");
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [tradeCount, setTradeCount] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);

  // Load chat history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CHAT_STORAGE_KEY);
      if (saved) setChatHistory(JSON.parse(saved));
    } catch {}
  }, []);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chatHistory));
    } catch {}
  }, [chatHistory]);

  // Scroll to bottom of chat when new entry added
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  // Check AI availability on mount
  useEffect(() => {
    axios.get("/api/ai/status").then(res => {
      setAiStatus(res.data);
    }).catch(() => {
      setAiStatus({ available: false, hasApiKey: false });
    });
  }, []);

  // Fetch trade count preview
  const fetchTradeCount = useCallback(async () => {
    try {
      const res = await axios.get("/api/monitor/status", { timeout: 5000 });
      if (res.data.success) {
        setTradeCount(res.data.trades?.length || 0);
      }
    } catch {
      setTradeCount(0);
    }
  }, []);

  useEffect(() => {
    if (source === "live") fetchTradeCount();
  }, [source, fetchTradeCount]);

  const handleAnalyze = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const body = {
        provider,
        model: model || undefined,
        source,
        analysisType,
        strategy: strategy !== "all" ? strategy : undefined,
        date: analysisType === "day" ? date : undefined,
        customPrompt: customPrompt.trim() || undefined,
      };
      const res = await axios.post("/api/ai/analyze", body, { timeout: 120000 });
      if (res.data.success) {
        const data = res.data.data;
        setResult(data);
        // Add to chat history
        const entry = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          provider: data.provider || provider,
          model: data.model || model || "?",
          prompt: customPrompt.trim() || `Full Analysis (${analysisType})`,
          analysisType,
          strategy,
          analysis: data.analysis,
          metrics: data.metrics,
          tradeCount: data.tradeCount,
          tokensUsed: data.tokensUsed,
        };
        setChatHistory(prev => [...prev, entry]);
        // Refresh token usage
        axios.get("/api/ai/status").then(r => { if (r.data) setAiStatus(r.data); }).catch(() => {});
      } else {
        setError(res.data.message || "Analysis failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Analysis request failed");
    }
    setLoading(false);
  };

  const clearHistory = () => {
    if (window.confirm("Clear all AI chat history?")) {
      setChatHistory([]);
      localStorage.removeItem(CHAT_STORAGE_KEY);
    }
  };

  const statusColor = aiStatus?.available && aiStatus?.hasApiKey ? "#4ade80" : "#f87171";
  const statusText = !aiStatus
    ? "Checking..."
    : !aiStatus.available
    ? "SDK not installed"
    : !aiStatus.hasApiKey
    ? "API key missing"
    : "Ready";

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>Analyze with AI</h2>
          <p className="dashboard-subtitle">
            AI-powered trade analysis (OpenAI GPT-4o / Zhipu GLM-4) — get insights, patterns, and improvement suggestions
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: statusColor,
            }}
          />
          <span style={{ fontSize: 12, color: statusColor }}>{statusText}</span>
        </div>
      </div>

      {/* Setup warning */}
      {aiStatus && (!aiStatus.available || !aiStatus.hasApiKey) && (
        <div
          style={{
            background: "var(--tint-indigo)",
            border: "1px solid #4338ca",
            borderRadius: 8,
            padding: "16px 20px",
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 600, color: "var(--pale-indigo)", marginBottom: 8 }}>Setup Required</div>
          {!aiStatus.available && (
            <div style={{ color: "var(--pale-indigo2)", fontSize: 13, marginBottom: 6 }}>
              1. Install the OpenAI SDK (works for both providers):{" "}
              <code style={{ background: "var(--tint-indigo-strong)", padding: "2px 6px", borderRadius: 3 }}>
                pip install openai
              </code>
            </div>
          )}
          {!aiStatus.hasApiKey && (
            <div style={{ color: "var(--pale-indigo2)", fontSize: 13 }}>
              {aiStatus.available ? "1" : "2"}. Add your API key to{" "}
              <code style={{ background: "var(--tint-indigo-strong)", padding: "2px 6px", borderRadius: 3 }}>
                server/.env
              </code>
              :{" "}
              <code style={{ background: "var(--tint-indigo-strong)", padding: "2px 6px", borderRadius: 3 }}>
                OPENAI_API_KEY=sk-... and/or GLM_API_KEY=your-glm-key
              </code>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          background: "var(--panel2)",
          border: "1px solid var(--panel3)",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 16,
        }}
      >
        {/* Row 1: Provider, Source, Strategy, Analysis Type */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12, alignItems: "flex-end" }}>
          {/* AI Provider */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              AI Provider
            </label>
            <div className="ema-filters" style={{ gap: 4 }}>
              {Object.entries(aiStatus?.providers || {}).map(([key, p]) => (
                <button key={key}
                  className={`filter-chip ${provider === key ? "active" : ""}`}
                  onClick={() => { setProvider(key); setModel(""); }}
                  style={p.ready ? {} : { opacity: 0.5 }}
                  title={p.ready ? `${p.name} ready` : `${p.name} not configured`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Model Selector */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              Model
            </label>
            <select className="ema-select" value={model || (aiStatus?.providers?.[provider]?.defaultModel || "")}
              onChange={e => setModel(e.target.value)}>
              {(aiStatus?.providers?.[provider]?.models || []).map(m => (
                <option key={m} value={m}>{m}{m === aiStatus?.providers?.[provider]?.defaultModel ? " (default)" : ""}</option>
              ))}
            </select>
          </div>

          {/* Token Usage */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              Tokens Used (Lifetime)
            </label>
            <div style={{ fontSize: 13, color: "var(--text2)", fontWeight: 600 }}>
              {(aiStatus?.tokenUsage?.total || 0).toLocaleString()}
              {aiStatus?.tokenUsage?.byProvider && Object.keys(aiStatus.tokenUsage.byProvider).length > 0 && (
                <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 400, marginLeft: 6 }}>
                  ({Object.entries(aiStatus.tokenUsage.byProvider).map(([k, v]) => `${k}: ${v.toLocaleString()}`).join(" | ")})
                </span>
              )}
            </div>
          </div>

          {/* Source */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              Data Source
            </label>
            <div className="ema-filters" style={{ gap: 4 }}>
              <button
                className={`filter-chip ${source === "live" ? "active" : ""}`}
                onClick={() => setSource("live")}
              >
                Live Trades{tradeCount !== null && source === "live" ? ` (${tradeCount})` : ""}
              </button>
              <button
                className={`filter-chip ${source === "backtest" ? "active" : ""}`}
                onClick={() => setSource("backtest")}
              >
                Backtest
              </button>
            </div>
          </div>

          {/* Strategy */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              Strategy
            </label>
            <select
              className="ema-select"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
            >
              {STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Analysis Type */}
          <div>
            <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
              Analyze
            </label>
            <select
              className="ema-select"
              value={analysisType}
              onChange={(e) => setAnalysisType(e.target.value)}
            >
              {ANALYSIS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date picker (shown for "day" type) */}
          {analysisType === "day" && (
            <div>
              <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
                Date
              </label>
              <input
                type="date"
                className="search-input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ width: 160 }}
              />
            </div>
          )}
        </div>

        {/* Row 2: Ask AI / Custom prompt */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: "var(--dim2)", display: "block", marginBottom: 4 }}>
            {customPrompt.trim() ? "Your Question (AI will answer based on your trade data)" : "Ask AI a question, or leave empty for full analysis"}
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea
              className="search-input"
              placeholder="Ask anything... e.g. 'Why am I losing on LONG trades?', 'Which day was most profitable?', 'Should I increase my stop loss?', 'What is the best time to trade?'"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && customPrompt.trim()) { e.preventDefault(); handleAnalyze(); } }}
              rows={2}
              style={{
                flex: 1,
                resize: "vertical",
                fontFamily: "inherit",
                minHeight: 44,
                maxHeight: 120,
              }}
            />
            <button
              className="btn btn-primary"
              onClick={handleAnalyze}
              disabled={loading || (aiStatus && (!aiStatus.available || !aiStatus.hasApiKey))}
              style={{ minWidth: 120, padding: "10px 16px", height: 44, whiteSpace: "nowrap" }}
            >
              {loading ? "Thinking..." : customPrompt.trim() ? "Ask AI" : "Full Analysis"}
            </button>
          </div>
        </div>

        {/* Status row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {loading && (
            <span style={{ fontSize: 13, color: "var(--dim2)" }}>
              {customPrompt.trim() ? "AI is thinking about your question..." : "AI is analyzing your trades... this may take 15-30 seconds"}
            </span>
          )}
          {result && !loading && (
            <span style={{ fontSize: 12, color: "var(--muted2)" }}>
              {result.provider || "AI"} / {result.model || "?"} | {result.tokensUsed?.toLocaleString()} tokens | Lifetime total: {(result.totalTokensUsed || 0).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div
          style={{
            background: "#1c1917",
            border: "1px solid #dc2626",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
            color: "var(--pale-neg)",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Chat History */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)" }}>
          Conversation History ({chatHistory.length})
        </span>
        {chatHistory.length > 0 && (
          <button
            className="btn btn-export"
            onClick={clearHistory}
            style={{ fontSize: 11, color: "#f87171", borderColor: "#f87171" }}
          >
            Clear History
          </button>
        )}
      </div>

      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          borderRadius: 8,
          border: "1px solid var(--panel3)",
          background: "var(--chart-bg)",
          padding: chatHistory.length > 0 ? "12px 16px" : "0",
        }}
      >
        {chatHistory.length === 0 && !loading && !error && (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "var(--faint2)",
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.4 }}>AI</div>
            <div style={{ fontSize: 15, marginBottom: 8 }}>No conversations yet</div>
            <div style={{ fontSize: 12 }}>
              Select your options above and click "Full Analysis" or ask a question to get started
            </div>
          </div>
        )}

        {chatHistory.map((entry, idx) => (
          <div
            key={entry.id || idx}
            style={{
              marginBottom: idx < chatHistory.length - 1 ? 20 : 0,
              paddingBottom: idx < chatHistory.length - 1 ? 20 : 0,
              borderBottom: idx < chatHistory.length - 1 ? "1px solid var(--panel3)" : "none",
            }}
          >
            {/* Prompt bubble */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <div
                style={{
                  background: "#1e3a5f",
                  borderRadius: "12px 12px 4px 12px",
                  padding: "10px 14px",
                  maxWidth: "80%",
                  fontSize: 13,
                  color: "var(--text2)",
                }}
              >
                <div style={{ fontSize: 10, color: "var(--muted2)", marginBottom: 4 }}>
                  {new Date(entry.timestamp).toLocaleString()} | {entry.provider}/{entry.model} | {entry.tradeCount} trades | {entry.tokensUsed?.toLocaleString()} tokens
                </div>
                <div style={{ fontWeight: 600 }}>{entry.prompt}</div>
              </div>
            </div>

            {/* Metrics cards (compact) */}
            {entry.metrics && entry.metrics.totalTrades > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8, paddingLeft: 8 }}>
                <span style={{ fontSize: 11, color: "var(--dim2)", background: "var(--panel3)", padding: "2px 8px", borderRadius: 4 }}>
                  {entry.metrics.totalTrades} trades
                </span>
                <span style={{ fontSize: 11, color: (entry.metrics.winRate || 0) >= 50 ? "#4ade80" : "#f87171", background: "var(--panel3)", padding: "2px 8px", borderRadius: 4 }}>
                  {(entry.metrics.winRate || 0).toFixed(1)}% win
                </span>
                <span style={{ fontSize: 11, color: (entry.metrics.totalPnl || 0) >= 0 ? "#4ade80" : "#f87171", background: "var(--panel3)", padding: "2px 8px", borderRadius: 4 }}>
                  P&L: {(entry.metrics.totalPnl || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                </span>
                <span style={{ fontSize: 11, color: "var(--dim2)", background: "var(--panel3)", padding: "2px 8px", borderRadius: 4 }}>
                  PF: {(entry.metrics.profitFactor || 0).toFixed(2)}
                </span>
              </div>
            )}

            {/* AI Response bubble */}
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  background: "var(--panel2)",
                  border: "1px solid var(--panel3)",
                  borderRadius: "12px 12px 12px 4px",
                  padding: "14px 18px",
                  maxWidth: "95%",
                  color: "var(--dim3)",
                  fontSize: 14,
                  lineHeight: 1.7,
                }}
              >
                {renderMarkdown(entry.analysis)}
                <div style={{ marginTop: 8, textAlign: "right" }}>
                  <button
                    className="btn btn-export"
                    onClick={() => {
                      const blob = new Blob([entry.analysis], { type: "text/markdown" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `ai-analysis-${new Date(entry.timestamp).toISOString().slice(0, 10)}.md`;
                      a.click();
                    }}
                    style={{ fontSize: 10 }}
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
    </div>
  );
}
